<template>
  <div class="personalized-settings-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <el-button @click="$router.push('/dashboard')" type="primary" plain style="margin-right: 10px;">
            返回首页
          </el-button>
          <span>个性化服务设置</span>
          <el-button type="primary" @click="handleAdd">添加个性化服务</el-button>
        </div>
      </template>
      
      <el-table :data="serviceList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="service_type" label="服务类型" width="180" />
        <el-table-column prop="description" label="描述" show-overflow-tooltip />
        <el-table-column prop="base_price" label="基础价格(元)" width="100" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="Number(scope.row.status) === 1 ? 'success' : 'danger'">
              {{ Number(scope.row.status) === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180" />
        <el-table-column prop="updated_at" label="更新时间" width="180" />
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加个性化服务' : '编辑个性化服务'" 
      v-model="dialogVisible"
      width="500px"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="服务类型" prop="service_type">
          <el-input v-model="form.service_type" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="基础价格" prop="base_price">
          <el-input-number v-model="form.base_price" :precision="2" :step="0.1" :min="0" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'PersonalizedSettings',
  setup() {
    const loading = ref(false)
    const serviceList = ref([])
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const formRef = ref(null)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    
    const form = ref({
      service_type: '',
      description: '',
      base_price: 0,
      status: 1
    })
    
    const rules = {
      service_type: [{ required: true, message: '请输入服务类型', trigger: 'blur' }],
      description: [{ required: true, message: '请输入描述', trigger: 'blur' }],
      base_price: [{ required: true, message: '请输入基础价格', trigger: 'blur' }]
    }
    
    const fetchServiceList = async () => {
      loading.value = true
      try {
        const response = await fetch(`/api/custom_service_settings.php?page=${currentPage.value}&pageSize=${pageSize.value}`)
        const data = await response.json()
        if (data.code === 0) {
          serviceList.value = data.data.list
          total.value = data.data.total
        } else {
          ElMessage.error(data.msg || '获取个性化服务列表失败')
        }
      } catch (error) {
        console.error('获取个性化服务列表失败:', error)
        ElMessage.error('获取个性化服务列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleAdd = () => {
      form.value = {
        service_type: '',
        description: '',
        base_price: 0,
        status: 1
      }
      dialogType.value = 'add'
      dialogVisible.value = true
    }
    
    const handleEdit = (row) => {
      form.value = { ...row }
      dialogType.value = 'edit'
      dialogVisible.value = true
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除该个性化服务内容吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/custom_service_settings_delete.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success('删除成功')
          fetchServiceList()
        } else {
          ElMessage.error(data.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        const url = dialogType.value === 'add' ? '/api/custom_service_settings_add.php' : '/api/custom_service_settings_update.php'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(form.value)
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(dialogType.value === 'add' ? '添加成功' : '更新成功')
          dialogVisible.value = false
          fetchServiceList()
        } else {
          ElMessage.error(data.msg || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
        }
      } catch (error) {
        console.error('提交失败:', error)
        ElMessage.error('提交失败')
      }
    }
    
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchServiceList()
    }
    
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchServiceList()
    }
    
    onMounted(() => {
      fetchServiceList()
    })
    
    return {
      loading,
      serviceList,
      dialogVisible,
      dialogType,
      formRef,
      form,
      rules,
      currentPage,
      pageSize,
      total,
      fetchServiceList,
      handleAdd,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.personalized-settings-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style> 